import React, { useEffect, useRef } from 'react';
import { songGainAt, songPosAt } from '../utils/movie';
import { httpsify } from '../utils/url';

// Gated diagnostics: set `window.__CS_AUDIO_DEBUG = true` in the console to
// trace song-sync behavior (rate nudges, hard resyncs, network stalls, blob
// prebuffer swaps) without shipping noise to normal sessions.
const sdbg = (...args) => {
  if (typeof window !== 'undefined' && window.__CS_AUDIO_DEBUG) {
    // eslint-disable-next-line no-console
    console.debug('[songsync]', ...args);
  }
};

// Session-scoped cache of fully-downloaded song files (url -> blob object
// URL). Songs are a few MB; playing from a local blob makes mid-song network
// rebuffering — an audible dropout that can hit ANYWHERE in the timeline, not
// just at clip cuts — impossible. Small FIFO cap; evicted entries revoked.
const songBlobCache = new Map();
const songBlobFetches = new Map(); // url -> in-flight Promise
const SONG_BLOB_CACHE_MAX = 8;

function fetchSongBlob(url) {
  if (songBlobCache.has(url)) return Promise.resolve(songBlobCache.get(url));
  if (songBlobFetches.has(url)) return songBlobFetches.get(url);
  const p = fetch(url)
    .then((r) => (r.ok ? r.blob() : null))
    .then((b) => {
      if (!b) return null;
      const obj = URL.createObjectURL(b);
      songBlobCache.set(url, obj);
      while (songBlobCache.size > SONG_BLOB_CACHE_MAX) {
        const [oldUrl, oldObj] = songBlobCache.entries().next().value;
        songBlobCache.delete(oldUrl);
        try { URL.revokeObjectURL(oldObj); } catch { /* noop */ }
      }
      return obj;
    })
    .catch(() => null)
    .finally(() => songBlobFetches.delete(url));
  songBlobFetches.set(url, p);
  return p;
}

let ytApiPromise;
export function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return ytApiPromise;
}

// One uploaded-MP3 track. Self-syncs to the shared clock so several can play
// (and crossfade) at once.
export function UploadTrack({ song, clockRef }) {
  const ref = useRef(null);
  // Consecutive frames the element has been out of sync (see loop below).
  const driftFramesRef = useRef(0);
  // Last seen clock epoch — bumped by the player on explicit user seeks.
  const epochRef = useRef(null);
  // Rate-nudge hysteresis: 0 = off (rate 1), else the engaged rate value.
  const nudgeRef = useRef(0);
  const lastRateChangeRef = useRef(0);
  // Fully-downloaded blob URL waiting for a silent moment to swap in.
  const pendingBlobRef = useRef(null);

  const netUrl = httpsify(song.url);

  // Prime the element to its starting position as soon as it loads so the first
  // play doesn't stall while it seeks + buffers that spot (music-late-on-start).
  const onMeta = () => {
    const a = ref.current;
    if (!a) return;
    // The sync nudge below is a fixed ±2% rate change. With time-stretch
    // (preservesPitch) the browser's stretcher can warble/click on music, so
    // run it as a plain rate change instead — a 2% pitch shift (~⅓ semitone)
    // is far less audible than stretcher artifacts. At rate 1 this is a no-op.
    try { a.preservesPitch = false; } catch { /* noop */ }
    try { a.webkitPreservesPitch = false; } catch { /* noop */ }
    const target = songPosAt(song, clockRef.current.time || 0);
    if (Number.isFinite(target) && Math.abs(a.currentTime - target) > 0.3) {
      try { a.currentTime = target; } catch { /* noop */ }
    }
  };

  // Prebuffer the WHOLE song into a local blob (a few MB) and play from it, so
  // a mid-song network rebuffer — an audible dropout anywhere on the timeline —
  // can't happen. Until the blob lands the element streams from the network;
  // if the track is already sounding, the swap defers to a silent moment.
  useEffect(() => {
    let cancelled = false;
    pendingBlobRef.current = null;
    if (!netUrl) return undefined;
    fetchSongBlob(netUrl).then((obj) => {
      if (cancelled || !obj) return;
      const a = ref.current;
      if (!a || a.src === obj) return;
      if (a.paused) {
        try {
          a.src = obj;
          a.load(); // onMeta re-primes the position from the clock
        } catch { /* noop */ }
        sdbg('prebuffered -> playing from blob', { song: song.id });
      } else {
        pendingBlobRef.current = obj;
        sdbg('prebuffered (swap deferred until silent)', { song: song.id });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [netUrl, song.id]);

  // Autoplay-policy blessing: iOS gates programmatic play() per-element until
  // it has been played once inside a user gesture. The player dispatches
  // `cs:prime-audio` from its play button; a silent play→pause here marks the
  // element user-activated so the clock loop's play() calls work thereafter.
  useEffect(() => {
    const prime = () => {
      const a = ref.current;
      if (!a || !a.paused) return;
      const vol = a.volume;
      a.volume = 0;
      const p = a.play();
      if (p && p.then) {
        p.then(() => {
          // Only park it again if it shouldn't be sounding right now — when it
          // should, the clock loop takes over the already-rolling element
          // (pausing here would fight the loop's own play() for a frame).
          const { time, playing, dur } = clockRef.current;
          if (!playing || songGainAt(song, time || 0, dur) <= 0) {
            try { a.pause(); } catch { /* noop */ }
          }
          a.volume = vol;
        }).catch(() => { a.volume = vol; });
      } else {
        a.volume = vol;
      }
    };
    window.addEventListener('cs:prime-audio', prime);
    return () => window.removeEventListener('cs:prime-audio', prime);
  }, [song, clockRef]);

  useEffect(() => {
    let raf;
    const loop = () => {
      const a = ref.current;
      const { time, playing, master, mode, dur, epoch } = clockRef.current;
      if (a) {
        const g = mode === 'composed' ? songGainAt(song, time, dur) * master : 0;
        const epochChanged = epochRef.current !== null && epoch !== epochRef.current;
        epochRef.current = epoch;
        // Complete a deferred blob swap whenever nothing is audible.
        if (pendingBlobRef.current && (g <= 0 || a.paused || !playing)) {
          const obj = pendingBlobRef.current;
          pendingBlobRef.current = null;
          try {
            a.src = obj;
            a.load();
          } catch { /* noop */ }
          sdbg('blob swapped in at silent moment', { song: song.id });
        }
        if (g <= 0) {
          if (!a.paused) a.pause();
          driftFramesRef.current = 0;
        } else {
          // Skip sub-0.3% volume writes — no point dithering the gain.
          const vol = Math.min(1, g);
          if (Math.abs((a.volume == null ? 1 : a.volume) - vol) > 0.003) a.volume = vol;
          const target = songPosAt(song, time);
          if (Number.isFinite(target)) {
            const drift = a.currentTime - target; // + = song ahead of the clock
            if (epochChanged || !playing || a.paused) {
              // Explicit user seek, or nothing audible rolling yet: snap the
              // position exactly — a seek here can't be heard.
              if (Math.abs(drift) > 0.05) {
                try { a.currentTime = target; } catch { /* noop */ }
              }
              if (nudgeRef.current) {
                nudgeRef.current = 0;
                try { a.playbackRate = 1; } catch { /* noop */ }
              }
              driftFramesRef.current = 0;
            } else {
              // Mid-playback: NEVER seek for small drift (a currentTime yank is
              // an audible blip). Converge with a gentle rate nudge instead —
              // but dead-banded and hysteretic: engage a FIXED ±2% only once
              // drift exceeds 250ms, release once it's back under 60ms, and
              // change the element's rate at most once a second. (The previous
              // continuously-recomputed rate rewrote playbackRate nearly every
              // frame as drift jittered — retriggering the browser's audio
              // pipeline constantly, itself a plausible blip source.)
              const now = performance.now();
              const engaged = nudgeRef.current;
              let want = engaged;
              if (!engaged && Math.abs(drift) > 0.25) {
                want = drift > 0 ? 0.98 : 1.02;
              } else if (engaged && (Math.abs(drift) < 0.06 || (engaged < 1) !== (drift > 0))) {
                want = 0;
              }
              if (want !== engaged && now - lastRateChangeRef.current > 1000) {
                nudgeRef.current = want;
                lastRateChangeRef.current = now;
                try { a.playbackRate = want || 1; } catch { /* noop */ }
                sdbg(want ? 'nudge ENGAGE' : 'nudge release', {
                  song: song.id,
                  drift: Math.round(drift * 1000) / 1000,
                  rate: want || 1,
                });
              }
              if (Math.abs(drift) > 1.2) {
                // Real desync (rare): hard-seek, but only after a few
                // consecutive frames so one glitched clock read can't yank.
                driftFramesRef.current += 1;
                if (driftFramesRef.current >= 3) {
                  sdbg('HARD RESYNC', { song: song.id, drift, target });
                  try { a.currentTime = target; } catch { /* noop */ }
                  try { a.playbackRate = 1; } catch { /* noop */ }
                  nudgeRef.current = 0;
                  driftFramesRef.current = 0;
                }
              } else {
                driftFramesRef.current = 0;
              }
            }
          }
          if (playing && a.paused) a.play().catch(() => {});
          if (!playing && !a.paused) a.pause();
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [song, clockRef]);

  return (
    <audio
      ref={ref}
      src={netUrl}
      preload="auto"
      onLoadedMetadata={onMeta}
      onWaiting={() => sdbg('element WAITING (mid-song network stall)', { song: song.id })}
      onStalled={() => sdbg('element stalled (network)', { song: song.id })}
    />
  );
}

// One YouTube track used purely as an audio source.
export function YouTubeTrack({ song, clockRef }) {
  const divRef = useRef(null);
  const playerRef = useRef(null);
  const readyRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    let player;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !divRef.current) return;
      player = new YT.Player(divRef.current, {
        videoId: song.videoId,
        playerVars: { controls: 0, disablekb: 1, modestbranding: 1, rel: 0, playsinline: 1 },
        events: {
          onReady: () => {
            playerRef.current = player;
            readyRef.current = true;
          },
        },
      });
    });
    // Autoplay-policy blessing, YouTube edition: iframe playback needs a user
    // gesture just like <audio> (iOS especially — without this the overlay song
    // is simply SILENT on a published movie, whose music is never baked into
    // the HLS). The player's play button dispatches `cs:prime-audio`; a muted
    // play→pause inside that gesture user-activates the iframe so the clock
    // loop's playVideo() calls work from then on.
    let primed = false;
    const prime = () => {
      const yt = playerRef.current;
      if (primed || !yt || !readyRef.current) return;
      try {
        const st = yt.getPlayerState ? yt.getPlayerState() : -1;
        if (st === 1) { primed = true; return; } // already rolling
        yt.mute();
        yt.playVideo();
        setTimeout(() => {
          try {
            const { time, playing, dur } = clockRef.current;
            const g = songGainAt(song, time || 0, dur);
            if (!playing || g <= 0) yt.pauseVideo();
            yt.unMute();
          } catch { /* noop */ }
        }, 250);
        primed = true;
      } catch { /* noop */ }
    };
    window.addEventListener('cs:prime-audio', prime);
    let raf;
    let driftFrames = 0;
    let lastEpoch = null;
    const loop = () => {
      const yt = playerRef.current;
      if (yt && readyRef.current) {
        const { time, playing, master, mode, dur, epoch } = clockRef.current;
        const g = mode === 'composed' ? songGainAt(song, time, dur) * master : 0;
        const epochChanged = lastEpoch !== null && epoch !== lastEpoch;
        lastEpoch = epoch;
        try {
          if (g <= 0) {
            if (yt.getPlayerState && yt.getPlayerState() === 1) yt.pauseVideo();
            driftFrames = 0;
          } else {
            yt.setVolume(Math.round(Math.min(1, g) * 100));
            const target = songPosAt(song, time);
            const cur = yt.getCurrentTime ? yt.getCurrentTime() : 0;
            const drift = Math.abs(cur - target);
            if (epochChanged || !playing) {
              // Explicit user seek / paused: correct silently-enough right away.
              if (drift > 0.3) yt.seekTo(target, true);
              driftFrames = 0;
            } else if (drift > 0.8) {
              // Same sustained-drift guard as UploadTrack: don't yank the song
              // for transient clock jitter at clip switches.
              driftFrames += 1;
              if (drift > 2.5 || driftFrames >= 8) {
                yt.seekTo(target, true);
                driftFrames = 0;
              }
            } else {
              driftFrames = 0;
            }
            if (playing) yt.playVideo();
            else yt.pauseVideo();
          }
        } catch {
          /* noop */
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('cs:prime-audio', prime);
      readyRef.current = false;
      playerRef.current = null;
      try {
        if (player && player.destroy) player.destroy();
      } catch {
        /* noop */
      }
    };
  }, [song.videoId, song, clockRef]);
  return (
    <div style={{ position: 'absolute', left: -99999, top: 0, width: 320, height: 180, pointerEvents: 'none' }}>
      <div ref={divRef} />
    </div>
  );
}
